import '../proposal-animations.css';

import { useQueryClient } from '@tanstack/react-query';
import { Check, Inbox01, X } from '@untitledui/icons';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  GroupedList,
  type GroupedListScrollAnchor,
  type GroupedListSection,
} from '@/lib/base/grouped-list';
import { PageLayout } from '@/lib/base/page-layout';
import { TableCard } from '@/lib/base/table';
import { QueryErrorState } from '@/lib/components';
import { type BulkAction, BulkActionBar } from '@/lib/crm/list';
import {
  type AiActionsView,
  DEFAULT_AI_ACTIONS_VIEW,
} from '@/lib/localStorage/aiActionsSortPrefs';
import { useViewHealthMarker } from '@/lib/view-health';

import { useOrg } from '../../../contexts/OrgContext';
import type {
  ProposalDetail,
  ProposalListItem,
  ProposalsListResponse,
} from '../../../lib/proposals/core/types';
import {
  PROPOSALS_PAGE_SIZE,
  useMyPendingProposalsPaginated,
} from '../../../lib/proposals/hooks/useProposals';
import { useBulkMarkReadProposals } from '../../../lib/proposals/query/mutations';
import { proposalDetailQuery } from '../../../lib/proposals/query/queries';
import { isOnboardingProposal } from '../../../lib/proposals/utils/onboardingConfig';
import { ActionsEmptyState } from '../ActionsEmptyState';
import { ActionTableRow } from '../ActionTableRow';
import AgentFeedRow from '../AgentFeedRow';
import { BulkResolutionConfirmDialogs } from '../BulkResolutionConfirmDialogs';
import { GeneratingActionsIndicator } from '../GeneratingActionsIndicator';
import { GroupTrailingControls } from '../GroupTrailingControls';
import { OnboardingActionRow } from '../OnboardingActionRow';
import { OnboardingRetryNotice } from '../OnboardingRetryNotice';
import { OnboardingSectionHeader } from '../OnboardingSectionHeader';
import { ProposalHistoryList } from '../ProposalHistoryList';
import RecipientAvatar from '../RecipientAvatar';
import { RowHoverActions } from '../RowHoverActions';
import { ActionListSkeleton } from '../shared/ActionListSkeleton';
import type { PriorityGroup } from '../utils/groupProposalsByTier';
import { groupProposalsByTier } from '../utils/groupProposalsByTier';
import { Toolbar } from './Toolbar';
import { RESOLUTION_FOR_VIEW } from './types';
import { useBulkResolution } from './useBulkResolution';
import { useNewActionAnimation } from './useNewActionAnimation';
import { useOnboardingState } from './useOnboardingState';

// Re-exported for existing importers; classification lives in
// utils/groupProposalsByTier so the history views (Done / Dismissed /
// Archived) render identical groupings.
export type {
  PriorityGroup,
  PriorityTier,
} from '../utils/groupProposalsByTier';
export type { PendingBulk } from './types';

// Use shared constant to ensure cache sharing with other proposal hooks.
// 500 covers most users' pending inbox; virtualization handles display efficiently.
const PAGE_SIZE = PROPOSALS_PAGE_SIZE;

/**
 * Last scroll position per list view — the row at the top of the viewport and
 * how far past it — so returning from an action lands the user where they
 * left off instead of at the top.
 *
 * Module-level on purpose. `/actions` and `/actions/:id` are separate routes
 * (see `dashboard.routes.tsx`), so opening an action UNMOUNTS this component
 * and going back mounts a fresh one — any offset held in state or a ref dies
 * with it. `dashboard.routes.tsx` documents that the legacy shell stayed
 * continuously mounted for exactly this reason.
 *
 * Keyed by view because Open / Done / Dismissed / Archived are different lists
 * and should not inherit each other's position. Deliberately NOT cleared on
 * unmount — surviving unmount is the entire point — and bounded by the fixed
 * set of view names, so it cannot grow.
 */
const SCROLL_ANCHOR_BY_VIEW = new Map<AiActionsView, GroupedListScrollAnchor>();

interface ActionListPanelProps {
  /** Which list this page shows. Driven entirely by the route
      (/actions → 'open', /actions/done → 'done', …) — there is no
      persisted view preference and no view adoption. */
  view?: AiActionsView;
}

function ActionListPanelComponent({
  view = DEFAULT_AI_ACTIONS_VIEW,
}: ActionListPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { currentOrg } = useOrg();
  const agentFeedEnabled = useFeatureFlag('agent_feed');
  const isHistoryView = view !== 'open';
  const bulkMarkReadMutation = useBulkMarkReadProposals(currentOrg?.id ?? 0);

  // Note: there used to be a 5-second `invalidateQueries` setInterval here
  // that ran during onboarding to surface newly-created proposals. It has
  // been replaced by the proposal.created WebSocket push delivered to the
  // assignee — see lib/proposals/realtime/. If the WS push starts dropping
  // proposals (e.g., long disconnect during onboarding), bumping
  // `refetchOnReconnect` on the proposal list query is the right next step
  // before reaching for setInterval again.

  // Fetch all proposals in one query — groups handle display
  const pageSize = PAGE_SIZE;
  const {
    data: proposalsResponse,
    isLoading: loading,
    error,
    isFetching,
  } = useMyPendingProposalsPaginated(
    currentOrg?.id ?? 0,
    1,
    pageSize,
    undefined, // Always fetch all — groups handle display
    { enabled: !!currentOrg?.id }
  ) as {
    data: ProposalsListResponse | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
  };

  // View-health markers for the `/:orgId/actions` list routes (see
  // dashboard.routes.tsx). `actions-list-ready` / `actions-list-empty-ready`
  // are mutually exclusive: when the list query resolves, exactly one fires
  // based on result count. `actions-list-items-rendered` fires post-commit
  // once the list query has resolved with ≥1 item, so it reflects an actual
  // render commit rather than "data arrived".
  const listSettled = proposalsResponse !== undefined;
  const listHasItems = (proposalsResponse?.results?.length ?? 0) > 0;
  useViewHealthMarker('actions-list-ready', {
    ready: listSettled && listHasItems,
  });
  useViewHealthMarker('actions-list-empty-ready', {
    ready: listSettled && !listHasItems,
  });
  useViewHealthMarker('actions-list-items-rendered', {
    ready: listHasItems,
  });

  // Track which proposals we've already prefetched to avoid duplicate requests
  const prefetchedIdsRef = useRef<Set<number>>(new Set());

  const newActionIds = useNewActionAnimation(proposalsResponse?.results);

  const {
    onboardingStatus,
    onboardingMessages,
    isWaitingForPipeline,
    onboardingCount,
    onboardingCategory,
    showPinnedOnboarding,
    isOnboardingActive,
    hasEverLoadedRef,
  } = useOnboardingState({
    orgId: currentOrg?.id,
    proposalsResponse,
    isFetching,
  });

  const {
    approvingTier,
    selectedIds,
    selectedIdStrings,
    handleSelectionChange,
    handleClearSelection,
    requestApproveGroup,
    requestDismissGroup,
    requestApproveSingle,
    requestDismissSingle,
    requestApproveSelected,
    requestDismissSelected,
    pendingApprove,
    pendingRisk,
    pendingCount,
    handleConfirmApprove,
    handleCancelApprove,
    pendingDismiss,
    dismissCount,
    handleConfirmDismiss,
    handleCancelDismiss,
  } = useBulkResolution({
    orgId: currentOrg?.id,
    results: proposalsResponse?.results,
  });

  // Group non-onboarding proposals by priority tier
  const cachedPriorityGroupsRef = useRef<PriorityGroup[]>([]);
  const priorityGroups = useMemo<PriorityGroup[]>(() => {
    if (!proposalsResponse?.results) {
      return cachedPriorityGroupsRef.current;
    }

    // groupProposalsByTier sorts newest-first within each tier — see the
    // util's doc comment for the ordering rules.
    const groups = groupProposalsByTier(
      proposalsResponse.results.filter(p => !isOnboardingProposal(p))
    );

    cachedPriorityGroupsRef.current = groups;
    return groups;
  }, [proposalsResponse]);

  // The floating selection bar is the same `BulkActionBar` the CRM tables
  // use, so multi-select reads the same everywhere in the product. It takes
  // the selection as a string array and hands it back to each action; these
  // handlers already operate on the hook's `selectedProposals`, so the
  // argument is ignored.
  const selectedIdsArray = useMemo(
    () => [...selectedIdStrings],
    [selectedIdStrings]
  );
  const selectionBarActions = useMemo<BulkAction[]>(
    () => [
      {
        key: 'approve',
        label: 'Approve',
        icon: Check,
        onAction: requestApproveSelected,
        disabled: approvingTier !== null,
      },
      {
        key: 'dismiss',
        label: 'Dismiss',
        icon: X,
        onAction: requestDismissSelected,
        destructive: true,
        disabled: approvingTier !== null,
      },
    ],
    [requestApproveSelected, requestDismissSelected, approvingTier]
  );

  const unreadCount = useMemo(() => {
    if (!proposalsResponse?.results) {
      return 0;
    }
    return proposalsResponse.results.filter(p => p.read_at === null).length;
  }, [proposalsResponse?.results]);

  const handleMarkAllAsRead = useCallback(async () => {
    if (bulkMarkReadMutation.isPending || unreadCount === 0) {
      return;
    }
    try {
      await bulkMarkReadMutation.mutateAsync({});
      toast.success('All actions marked as read');
    } catch (err) {
      console.error('[actions] Failed to mark all as read', err, {
        orgId: currentOrg?.id,
      });
      toast.error("We couldn't mark all as read. Try again.");
    }
  }, [bulkMarkReadMutation, unreadCount, currentOrg?.id]);

  const handleActionClick = useCallback(
    (actionId: number) => {
      if (currentOrg) {
        // Carry the originating list path so the detail page's back chevron
        // returns HERE deterministically. Walking history (navigate(-1)) is
        // not reliable on the detail page: opening an entity slideout there
        // pushes a `?slideoutEntityId=` history entry and closing it replaces,
        // so -1 lands back on the same action URL.
        navigate(`/${currentOrg.id}/actions/${actionId}`, {
          state: { backPath: location.pathname },
        });
      }
    },
    [navigate, currentOrg, location.pathname]
  );

  const handleAgentFeedClick = useCallback(() => {
    if (currentOrg) {
      navigate(`/${currentOrg.id}/actions/agent-feed`, {
        state: { backPath: location.pathname },
      });
    }
  }, [navigate, currentOrg, location.pathname]);

  // Prefetch proposal detail on mousedown to get ~100-200ms head start before click completes.
  // TanStack Query dedupes requests, so if prefetch is in-flight when click navigates,
  // the detail page's useQuery will reuse the same request.
  const handleActionMouseDown = useCallback(
    (actionId: number) => {
      if (currentOrg) {
        const cached = queryClient.getQueryData(
          proposalDetailQuery(currentOrg.id, actionId).queryKey
        );
        if (!cached) {
          queryClient.prefetchQuery(
            proposalDetailQuery(currentOrg.id, actionId)
          );
        }
      }
    },
    [currentOrg, queryClient]
  );

  // True initial loading = never received data before AND either actively loading
  // or query hasn't started yet (e.g., waiting for currentOrg to be available).
  // This prevents skeleton from reappearing during segment/filter-triggered refetches,
  // while also preventing a flash of empty state before the query starts.
  const isInitialLoading =
    !hasEverLoadedRef.current && (loading || !currentOrg?.id);

  const getSourceIcon = useCallback(
    (action: ProposalListItem) => {
      let proposal: ProposalDetail | ProposalListItem = action;
      if (currentOrg) {
        const cached = queryClient.getQueryData<ProposalDetail>(
          proposalDetailQuery(currentOrg.id, action.id).queryKey
        );
        if (cached) {
          proposal = cached;
        }
      }
      // Compact avatar matching checkbox size for the row gutter
      return <RecipientAvatar action={proposal} size='xxs' />;
    },
    [currentOrg, queryClient]
  );

  // ── GroupedList wiring ───────────────────────────────────────────
  // Sections for the grouped table: priority groups (with per-group bulk
  // controls in the header band's trailing slot), newest-first within each
  // group. Virtualization lives inside GroupedList.
  const selectionActive = selectedIds.size > 0;
  const listSections = useMemo<GroupedListSection<ProposalListItem>[]>(
    () =>
      priorityGroups.map(group => ({
        id: group.tier,
        label: group.label,
        trailing: (
          <GroupTrailingControls
            group={group}
            approvingTier={approvingTier}
            selectionActive={selectionActive}
            onApprove={requestApproveGroup}
            onDismiss={requestDismissGroup}
          />
        ),
        items: group.actions,
      })),
    [
      priorityGroups,
      approvingTier,
      selectionActive,
      requestApproveGroup,
      requestDismissGroup,
    ]
  );

  const getProposalRowId = useCallback(
    (action: ProposalListItem) => String(action.id),
    []
  );
  const getProposalRowLabel = useCallback(
    (action: ProposalListItem) => action.title,
    []
  );
  const renderProposalRow = useCallback(
    (action: ProposalListItem) => (
      <ActionTableRow
        action={action}
        icon={getSourceIcon(action)}
        isNew={newActionIds.has(action.id)}
      />
    ),
    [getSourceIcon, newActionIds]
  );
  const renderProposalRowActions = useCallback(
    (action: ProposalListItem) => (
      <RowHoverActions
        action={action}
        disabled={approvingTier !== null}
        onApprove={requestApproveSingle}
        onDismiss={requestDismissSingle}
      />
    ),
    [approvingTier, requestApproveSingle, requestDismissSingle]
  );
  const handleRowClick = useCallback(
    (action: ProposalListItem) => {
      handleActionClick(action.id);
    },
    [handleActionClick]
  );

  // Prefetch proposal details for rows the virtualizer materializes —
  // enables instant navigation and provides avatar data without expensive
  // list serializer fields. The prefetched-ids ref dedupes across the
  // repeated onVisibleItemsChange invocations.
  const handleVisibleItemsChange = useCallback(
    (visible: ProposalListItem[]) => {
      if (!currentOrg) {
        return;
      }
      for (const action of visible) {
        if (!prefetchedIdsRef.current.has(action.id)) {
          prefetchedIdsRef.current.add(action.id);
          queryClient.prefetchQuery(
            proposalDetailQuery(currentOrg.id, action.id)
          );
        }
      }
    },
    [currentOrg, queryClient]
  );

  // Remember where the user was so returning from an action lands them back
  // there. Writes to a module-level map rather than state: this fires on every
  // scroll frame, and the value must survive this component unmounting — which
  // is exactly what happens, because /actions and /actions/:id are separate
  // routes that swap the list out for the detail view.
  const handleScrollAnchorChange = useCallback(
    (anchor: GroupedListScrollAnchor) => {
      SCROLL_ANCHOR_BY_VIEW.set(view, anchor);
    },
    [view]
  );

  // Onboarding rows render outside GroupedList (they're cards, not table
  // rows) — prefetch their details here so their icons resolve too.
  useEffect(() => {
    if (!currentOrg || !onboardingCategory) {
      return;
    }
    for (const action of onboardingCategory.actions) {
      if (!prefetchedIdsRef.current.has(action.id)) {
        prefetchedIdsRef.current.add(action.id);
        queryClient.prefetchQuery(
          proposalDetailQuery(currentOrg.id, action.id)
        );
      }
    }
  }, [currentOrg, onboardingCategory, queryClient]);

  // Flattened list of action IDs in display order for next action navigation
  // Includes onboarding IDs first (from pinned section), then regular actions
  const flattenedActionIds = useMemo(() => {
    const ids: number[] = [];

    // Add onboarding actions first if pinned section is showing
    if (showPinnedOnboarding && onboardingCategory) {
      onboardingCategory.actions.forEach(action => ids.push(action.id));
    }

    priorityGroups.forEach(group => {
      group.actions.forEach(action => ids.push(action.id));
    });
    return ids;
  }, [priorityGroups, showPinnedOnboarding, onboardingCategory]);

  if (error && !proposalsResponse?.results?.length) {
    return (
      <div className='flex justify-center items-center h-64'>
        <QueryErrorState error={error} label="We couldn't load proposals" />
      </div>
    );
  }

  const hasAnyContent = flattenedActionIds.length > 0;
  const isShowingSkeleton =
    isInitialLoading || (isFetching && !hasAnyContent && !showPinnedOnboarding);
  const isShowingEmptyState =
    !isShowingSkeleton &&
    !hasAnyContent &&
    !showPinnedOnboarding &&
    !isOnboardingActive &&
    hasEverLoadedRef.current;
  // Selection never touches the toolbar: the selection controls live in the
  // floating `BulkActionBar` below, which is portaled to `document.body` and
  // positioned over the page. Selecting a row therefore cannot reflow the
  // list — the toolbar's content is the same whether or not rows are
  // selected. (An earlier version swapped the toolbar's content for a "N
  // selected" row and needed a pinned min-height to stop the swap changing
  // the height; that path is gone rather than left behind a condition.)
  const hasSelection = view === 'open' && selectedIds.size > 0;

  const toolbarContent = (
    <Toolbar
      view={view}
      orgId={currentOrg?.id}
      unreadCount={unreadCount}
      onMarkAllAsRead={handleMarkAllAsRead}
      markAllAsReadPending={bulkMarkReadMutation.isPending}
    />
  );

  // Onboarding pipeline progress ("Preparing your actions…" / "Your first
  // actions are ready") renders beside the page title.
  const titleIndicator =
    view === 'open' &&
    !isShowingSkeleton &&
    (showPinnedOnboarding || isOnboardingActive) ? (
      <GeneratingActionsIndicator
        inline
        isActive={isOnboardingActive}
        waitingForPipeline={isWaitingForPipeline}
        messages={onboardingMessages}
      />
    ) : undefined;

  return (
    <PageLayout
      title='AI Actions'
      icon={<Inbox01 className='size-5 text-fg-quaternary' />}
      titleTrailing={titleIndicator}
      toolbar={toolbarContent}
      toolbarBorderBottom={false}
    >
      {/* Floating selection controls (portaled to body, fixed at bottom
            centre) — the same bar the CRM tables show on multi-select. */}
      {hasSelection && (
        <BulkActionBar
          selectedIds={selectedIdsArray}
          onClearSelection={handleClearSelection}
          additionalActions={selectionBarActions}
        />
      )}
      {/* History views (Done / Dismissed / Archived) get the same gutter +
            TableCard treatment as the Open list so every view renders the
            same table. */}
      {isHistoryView && (
        <div className='flex-1 min-h-0 flex flex-col px-4 pb-4 pt-2'>
          <TableCard.Root fillContainer>
            <ProposalHistoryList
              orgId={currentOrg?.id ?? 0}
              resolution={
                RESOLUTION_FOR_VIEW[view as Exclude<AiActionsView, 'open'>]
              }
            />
          </TableCard.Root>
        </div>
      )}
      {view === 'open' &&
        currentOrg?.id != null &&
        onboardingStatus?.steps != null && (
          <OnboardingRetryNotice
            orgId={currentOrg.id}
            steps={onboardingStatus.steps}
          />
        )}
      {/* Show skeleton loading state for initial load or when fetching with no data */}
      {view === 'open' && isShowingSkeleton && <ActionListSkeleton />}
      {view === 'open' && isShowingEmptyState && <ActionsEmptyState />}
      {/* Onboarding: pinned cards render above the grouped table (they're
            cards, not table rows). The pipeline-progress indicator renders
            beside the page title (titleTrailing) instead. */}
      {view === 'open' &&
        !isShowingSkeleton &&
        showPinnedOnboarding &&
        onboardingCategory && (
          <div className='shrink-0 max-h-[45%] overflow-y-auto'>
            <div role='region' aria-labelledby='onboarding-section'>
              <OnboardingSectionHeader remainingCount={onboardingCount} />
            </div>
            {onboardingCategory.actions.map(action => (
              <OnboardingActionRow
                key={action.id}
                action={action}
                isSelected={false}
                isNew={newActionIds.has(action.id)}
                showShortcuts={false}
                onMouseDown={handleActionMouseDown}
                onClick={handleActionClick}
              />
            ))}
          </div>
        )}

      {/* The grouped actions table — inset from the page edges and wrapped
            in the shared TableCard treatment (rounded corners + ring) so it
            reads as a card, matching the app's other data tables. */}
      {view === 'open' && !isShowingSkeleton && !isShowingEmptyState && (
        <div className='flex-1 min-h-0 flex flex-col px-4 pb-4 pt-2'>
          <TableCard.Root fillContainer>
            <GroupedList
              aria-label='AI actions'
              sections={listSections}
              getItemId={getProposalRowId}
              getItemLabel={getProposalRowLabel}
              renderItem={renderProposalRow}
              onItemClick={handleRowClick}
              selectable
              selectedIds={selectedIdStrings}
              onSelectionChange={handleSelectionChange}
              renderItemActions={renderProposalRowActions}
              onVisibleItemsChange={handleVisibleItemsChange}
              initialScrollAnchor={SCROLL_ANCHOR_BY_VIEW.get(view)}
              onScrollAnchorChange={handleScrollAnchorChange}
            />
          </TableCard.Root>
        </div>
      )}

      {/* Agent Feed — sticky footer pinned to the bottom of the page.
            mt-auto keeps it pinned even when the content above isn't a
            flex-1 block (skeleton / empty state). */}
      {agentFeedEnabled && (
        <div className='mt-auto flex-shrink-0'>
          <AgentFeedRow
            orgId={currentOrg?.id ?? null}
            isSelected={false}
            onClick={handleAgentFeedClick}
          />
        </div>
      )}

      <BulkResolutionConfirmDialogs
        pendingApprove={pendingApprove}
        pendingRisk={pendingRisk}
        pendingCount={pendingCount}
        onConfirmApprove={handleConfirmApprove}
        onCancelApprove={handleCancelApprove}
        pendingDismiss={pendingDismiss}
        dismissCount={dismissCount}
        onConfirmDismiss={handleConfirmDismiss}
        onCancelDismiss={handleCancelDismiss}
      />
    </PageLayout>
  );
}

ActionListPanelComponent.displayName = 'ActionListPanel';

// Wrap with memo to prevent unnecessary re-renders when parent state changes
// Use default shallow comparison for all props (selectedActionId, onActionSelect, isAgentFeedSelected)
const ActionListPanel = memo(ActionListPanelComponent);

export default ActionListPanel;
